<!--
@component
ViewsSwitch

See markup below.
-->

<script lang="ts">
  import scrollDirection from '$lib/components/common/Menu/scrollDirection';
  import type { MobileView } from './index.svelte';

  interface Props {
    mobileView: MobileView | undefined;
    hasStats?: boolean;
    hasLineups?: boolean;
    hasPhotos?: boolean;
    hasCommentary?: boolean;
  }

  let {
    mobileView = $bindable(),
    hasStats = false,
    hasLineups = false,
    hasPhotos = false,
    hasCommentary = false,
  }: Props = $props();

  let el = $state<HTMLDivElement | undefined>(undefined);

  const tabs = $derived(
    (
      [
        { id: 'commentary', label: 'Action', show: hasCommentary },
        { id: 'stats', label: 'Stats', show: hasStats },
        { id: 'lineups', label: 'Lineups', show: hasLineups },
        { id: 'photos', label: 'Photos', show: hasPhotos },
      ] as const
    ).filter((tab) => tab.show)
  );

  const handleScrollAfterClick = () => {
    if (!el?.classList.contains('is-sticky')) return;
    document
      .getElementById('match-view-scroll-target')
      ?.scrollIntoView({ behavior: 'instant', block: 'start' });
    // Counteracts the menu scroll behaviour... bit of a hack.
    setTimeout(() => {
      const menu = document.getElementById('top-menu');
      menu?.classList.toggle('up', false);
      menu?.classList.toggle('down', true);
    }, 50);
  };

  const selectView = (view: MobileView) => {
    mobileView = view;
    handleScrollAfterClick();
  };

  $effect(() => {
    if (!el) return;

    const observer = new IntersectionObserver(
      ([e]) => e.target.classList.toggle('is-sticky', e.intersectionRatio < 1),
      { threshold: [1] }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  });
</script>

<div bind:this={el}></div>
{#if mobileView}
  <div class="btn-container" use:scrollDirection>
    {#each tabs as { id, label } (id)}
      <button onclick={() => selectView(id)} class:active={mobileView === id}>
        {label}
      </button>
    {/each}
  </div>
{/if}

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  .btn-container {
    display: flex;
    max-width: 600px;
    width: 100%;
    margin: 0 auto;
    position: -webkit-sticky;
    position: sticky;
    top: 0px;
    z-index: 999;
    padding: 10px 0 10px;
    -webkit-backdrop-filter: blur(25px);
    backdrop-filter: blur(25px);
    transition: all 0.3s ease-out;
    &:global(.up) {
      top: -150px;
    }
    button {
      flex: 1;
      font-size: var(--theme-font-size-sm);
      @media (max-width: 450px) {
        font-size: var(--theme-font-size-xs);
      }
      padding-block: clamp(0.56rem, 0.52rem + 0.21vw, 0.69rem);
      letter-spacing: 0.03em;
      width: -moz-max-content;
      width: max-content;
      border: 1px solid var(--theme-colour-tan-grey);
      color: var(--theme-colour-text-secondary);
      background-color: var(--theme-colour-white);
      outline: none;
      cursor: pointer;
      &:first-child {
        border-top-left-radius: calc(10 * var(--theme-style-border-radius));
        border-bottom-left-radius: calc(10 * var(--theme-style-border-radius));
      }
      &:last-child {
        border-top-right-radius: calc(10 * var(--theme-style-border-radius));
        border-bottom-right-radius: calc(10 * var(--theme-style-border-radius));
      }
      &.active {
        background-color: var(--theme-colour-muted-grey);
        color: var(--theme-colour-text-primary);
        @include mixins.font-medium;
        filter: drop-shadow(
          0px 0.15rem 0.3rem var(--theme-style-soft-shadow-colour)
        );
      }
      &:hover {
        color: var(--theme-colour-text-primary);
        @include mixins.font-medium;
      }
    }
  }
</style>
